import { Component, inject } from '@angular/core';
import { MessageService } from 'primeng/api';
import { ComponentView } from 'src/modules/app-template/models/component-view.model';
import { MainAppService } from 'src/modules/app-template/services/main-app.service';
import { Objects } from 'src/modules/utils/shared/objects';
import { RestEndpoint } from '../../models/rest-endpoint.model';
import { FrontendField } from '../../netgen/FrontendField';
import { FrontendFieldType } from '../../netgen/FrontendFieldType';
import { FrontendForm } from '../../netgen/FrontendForm';
import { UserLoginDto } from '../../netgen/UserLoginDto';
import { UserSetInfoPriorToLoginDto } from '../../netgen/UserSetInfoPriorToLoginDto';
import { UserSetInfoPriorToLoginResultDto } from '../../netgen/UserSetInfoPriorToLoginResultDto';
import { AuthLoginResult } from '../../shared/AuthLoginResult';
import { FirstLoginComponent } from '../first-login/first-login.component';

@Component({
    selector: 'sm-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: false
})
export class LoginComponent extends ComponentView {

    app = inject(MainAppService);
    messageService = inject(MessageService);

    form: FrontendForm;
    inputInformationForm: FrontendForm;

    waiting = false;

    status: "login" | "inputInformation" = "login";

    async initParams(): Promise<boolean> {
        this.form = new FrontendForm([
            new FrontendField("userName", "Benutzername", FrontendFieldType.string, { 
                mandatory: true,
                captionAsPlaceholder: true,
                hasLabel: false
            }),
            new FrontendField("password", "Passwort", FrontendFieldType.password, { 
                mandatory: true,
                passwordFeedback: false,
                captionAsPlaceholder: true,
                hasLabel: false
            }),
            new FrontendField("secondFactor", "Bestätigungs-Code", FrontendFieldType.string, { 
                visible: false, 
                mandatory: true
            }),
            new FrontendField("send", "Login", FrontendFieldType.button, { 
                submitButton: true,
                icon: "fas fa-sign-in",
                onClick: this.send.bind(this)
            }),
            this.app.mainConfig.authRules.canRevokePassword ? new FrontendField("resetPassword", "Passwort vergessen", FrontendFieldType.button, {
                icon: "fas fa-lock",
                onClick: this.resetPassword.bind(this)
            }) : null,
            new FrontendField("waiting", "", FrontendFieldType.spinner, {
                visible: false,
                fixedHeight: "30px"
            })
        ], { submitOnEnter: true });
        
        return Promise.resolve(true);
    }

    async send(): Promise<void> {
        if (!this.app.validateForm(this.form, null, this.messageService)) {
            return;
        }
        let userName = this.form.getValueString("userName").trim();
        let password = this.form.getValueString("password").trim();
        this.form.getField("waiting").visible = true;
        this.form.getField("send").enabled = false;
        try {
            let loginUser = await this.app.auth.login(Objects.fromPlain(UserLoginDto, {
                userName, 
                password,
                secondFactor: this.form.getValueString("secondFactor")?.trim()
            }));
            switch (loginUser.loginResult) {
                case AuthLoginResult.ok:
                    try {
                        await this.app.navigateToUrl(this.route.snapshot.queryParams.returnUrl as string || '/');
                    }
                    catch {
                        await this.app.navigateTo(["/"]);
                    }
                    break;
                case AuthLoginResult.unspecifiedError:
                    this.app.showErrorToast("Beim Login kam es zu einem unbekannten Fehler", "Anmeldung fehlgeschlagen", null, this.messageService);
                    break;
                case AuthLoginResult.invalidUserName:
                case AuthLoginResult.invalidPassword:
                    this.app.showErrorToast("Die Kombination aus Benutzername und Passwort ist unbekannt", "Anmeldung fehlgeschlagen", null, this.messageService);
                    break;
                case AuthLoginResult.accountLocked:
                case AuthLoginResult.accountJustLocked:
                    this.app.showErrorToast("Ihr Benutzerkonto ist gesperrt. Bitte wenden Sie sich an einen Administrator", "Anmeldung fehlgeschlagen", null, this.messageService);
                    break;
                case AuthLoginResult.userInactive:
                    this.app.showErrorToast("Das Benutzerkonto wurde deaktiviert", "Anmeldung fehlgeschlagen", null, this.messageService);
                    break;
                case AuthLoginResult.mustChangePassword:
                case AuthLoginResult.answerQuestions:
                    FirstLoginComponent.currentPassword = password;
                    await this.app.navigateTo(['base', 'firstlogin'], null, { userName });
                    break;
              case AuthLoginResult.secondFactorNeeded:
                    this.form.getField("secondFactor").visible = true;
                    
                    this.app.showInfoToast("Wir haben Ihnen einen Bestätigungscode per E-Mail gesendet. Bitte geben Sie den Code ein, um den Login abzuschließen.", "Bestätigung notwendig", null, this.messageService);
                    break;    
                case AuthLoginResult.secondFactorInvalid:
                    this.form.setValue("secondFactor", "");
                    this.app.showErrorToast("Der Bestätigungs-Code ist ungültig - bitte versuchen Sie es erneut", null, null, this.messageService);
                    break;     
                case AuthLoginResult.secondFactorExpired:
                    this.form.setValue("secondFactor", "");
                    this.form.getField("secondFactor").visible = false;
                    this.app.showErrorToast("Der Bestätigungs-Code ist abgelaufen. Bitte melden Sie sich erneut an", null, null, this.messageService);
                    break;  
                case AuthLoginResult.needsInformation:
                    this.inputInformationForm = Objects.fromPlainUnsafe(FrontendForm, loginUser.loginResultData);
                    this.inputInformationForm.fields.push(new FrontendField("send", "Abschicken", FrontendFieldType.button, { 
                        submitButton: true,
                        icon: "fas fa-send",
                        onClick: this.sendInformation.bind(this)
                    }));
                    this.status = "inputInformation";
                    break;            
            }
        }
        finally {
            this.form.getField("waiting").visible = false;   
        this.form.getField("send").enabled = true;
        }
    }

    async sendInformation(): Promise<void> {
        if (!this.app.validateForm(this.inputInformationForm, null, this.messageService)) {
            return;
        }
        
        let data = Objects.fromPlain(UserSetInfoPriorToLoginDto, {
            userName: this.form.getValueString("userName").trim(), 
            password: this.form.getValueString("password").trim(),
            data: this.inputInformationForm.get({})
        });
        
        let res = await RestEndpoint.main().body(data).post().run("api/app/auth/setInfoPriorToLogin").get(UserSetInfoPriorToLoginResultDto);
        if (res.ok) {
            this.status = "login";
            this.app.showInfoToast(res.message, null, null, this.messageService);
            await this.send();
        }
        else {
            this.app.showErrorToast(res.message, null, null, this.messageService);
        }
    }
    
    async resetPassword(): Promise<void> {
        await this.app.navigate(["/base", "resetpassword"]);
    }
}
